import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@mui/material';

import {
  createPayoutAch,
  createPayoutCheck,
  updatePayoutAch,
  updatePayoutCheck,
} from '../../../api/clientsApi/clientsApi';
import FormCancelButton from '../../../components/buttons/FormCancelButton';
import FormSubmitButton from '../../../components/buttons/FormSubmitButton';
import FormMaskedTextField from '../../../components/formFields/FormMaskedTextField';
import FormSelectField from '../../../components/formFields/FormSelectField';
import FormTextField from '../../../components/formFields/FormTextField';
import { StatusChip } from '../../../components/StatusChip';
import { US_STATES_LIST } from '../../../constants/constants';
import useSnackBar from '../../../hooks/useSnackBar';
import { payoutAchSchema, payoutCheckSchema } from '../../../helpers/formSchemas/formSchemas';
import { ACCOUNT_TYPES, INITIAL_ACH, INITIAL_CHECK } from './constants';
import {
  buildAchCreatePayload,
  buildAchUpdatePayload,
  buildCheckPayload,
  buildCheckUpdatePayload,
  getMaskedAccountDisplay,
  getPayoutStatusChip,
  getStateLabel,
  hasSavedAchDetails,
  hasSavedCheckDetails,
  isCriticalProvisioningStatus,
  isProvisioningErrorStatus,
} from './helpers';
import { ErrorData } from '../../../types';
import {
  AchDraft,
  CheckDraft,
  DiffRow,
  ModalContext,
  PayoutFormSource,
  PayoutMethod,
} from './types';
import ClientPayoutSaveConfirmDialog from './ClientPayoutSaveConfirmDialog';

const useStyles = makeStyles()((theme: Theme) => ({
  fieldHeadline: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '24px',
  },
  sectionHeadline: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
  },
  formDescription: {
    color: 'rgba(255, 255, 255, 0.55)',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 400,
    marginTop: '4px',
  },
  payoutMethodButton: {
    minHeight: '44px',
    padding: '10px 24px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',

    '&:hover': {
      borderColor: theme.palette.secondary.dark,
      backgroundColor: 'transparent',
      color: theme.palette.text.disabled,
    },

    '&.Mui-disabled': {
      borderColor: theme.palette.secondary.dark,
      color: theme.palette.text.disabled,
      opacity: 1,
    },
  },
  activePayoutMethodButton: {
    borderColor: theme.palette.info.main,
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,

    '&:hover': {
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.info.main,
      color: theme.palette.common.white,
    },

    '&.Mui-disabled': {
      borderColor: theme.palette.info.main,
      backgroundColor: theme.palette.info.main,
      color: theme.palette.common.white,
      opacity: 1,
    },
  },
  payoutMethodLabel: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 600,
    letterSpacing: '0.5px',
    lineHeight: '22px',
  },
  formFieldsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formInfoContainer: {
    display: 'flex',
    gap: '16px',
    width: '100%',

    '@media (max-width: 600px)': {
      flexDirection: 'column',
    },
  },
  formFieldWrapper: {
    width: '100%',
  },
  displayTextField: {
    width: '100%',

    input: {
      padding: '13.5px 16px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },

    '& .MuiOutlinedInput-root': {
      minHeight: '48px',
      background: 'rgba(255, 255, 255, 0.03)',
      borderRadius: '12px',

      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.dark,
        borderRadius: '12px',
      },
    },

    '& .MuiOutlinedInput-input.Mui-disabled': {
      WebkitTextFillColor: theme.palette.common.white,
    },

    '& .MuiInputLabel-root': {
      color: theme.palette.text.disabled,
      fontFamily: 'Inter',
      fontSize: '14px',

      '&[data-shrink="false"]': {
        top: '-3px',
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },
  },
  /** ACH (left) and mailing address (right) side by side when both are on file. */
  methodColumns: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    columnGap: '32px',

    '@media (max-width: 900px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
      rowGap: '24px',
    },
  },
  secondMethodColumn: {
    paddingLeft: '32px',
    borderLeft: '1px solid rgba(255, 255, 255, 0.1)',

    '@media (max-width: 900px)': {
      paddingLeft: 0,
      paddingTop: '24px',
      borderLeft: 'none',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    border: '1px solid rgba(255, 217, 0, 0.2)',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 217, 0, 0.08)',
  },
  warningBannerText: {
    color: 'rgba(255, 217, 0, 0.9)',
    fontFamily: 'Inter',
    fontSize: '13px',
  },
  provisioningErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    border: '1px solid rgba(255, 217, 0, 0.2)',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 217, 0, 0.08)',
  },
  provisioningErrorText: {
    color: 'rgba(255, 217, 0, 0.9)',
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,
  },
  editButton: {
    padding: '4px 10px',
    borderRadius: '8px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',

    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    width: '320px',
    maxWidth: '100%',
    margin: '24px auto 0',
  },
  notifyToggleRoot: {
    marginRight: 0,
    color: theme.palette.common.white,

    '& .MuiFormControlLabel-label': {
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      whiteSpace: 'nowrap',
    },
  },
  notifyInactiveToggle: {
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.secondary.light,
    },

    '& .MuiSwitch-track': {
      backgroundColor: theme.palette.secondary.light,
    },
  },
  notifyActiveToggle: {
    '& .MuiSwitch-thumb': {
      backgroundColor: theme.palette.common.white,
    },

    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: theme.palette.info.main,
    },
  },
}));

interface ClientPayoutFormContainerProps {
  clientId: string;
  /** Saved payout data this form edits (memoise it — the form resets when it changes). */
  source: PayoutFormSource;
  reasonCodes: string[];
  canEdit: boolean;
  onSaved: () => void;
  /** When set, saves apply to this settlement only. */
  settlementId?: number;
  /** Open directly in edit mode (e.g. from a settlement row's Edit action). */
  initialEditing?: boolean;
  /** Called after the user cancels an edit. */
  onCancel?: () => void;
}

const ClientPayoutFormContainer = ({
  clientId,
  source,
  reasonCodes,
  canEdit,
  onSaved,
  settlementId,
  initialEditing = false,
  onCancel,
}: ClientPayoutFormContainerProps) => {
  const styles = useStyles();
  const { setAlert } = useSnackBar();

  const [selectedMethod, setSelectedMethod] = useState<PayoutMethod | null>(
    source.preferredMethod,
  );
  const [isEditing, setIsEditing] = useState(initialEditing && canEdit);
  const [notifyOnAch, setNotifyOnAch] = useState(true);
  const [notifyOnAddress, setNotifyOnAddress] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContext, setModalContext] = useState<ModalContext>('ach');
  const [saving, setSaving] = useState(false);

  const achFormMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(payoutAchSchema) as never,
    defaultValues: { ...INITIAL_ACH, confirmAccountNumber: '' },
  });

  const checkFormMethods = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: yupResolver(payoutCheckSchema) as never,
    defaultValues: { ...INITIAL_CHECK },
  });

  const {
    reset: resetAch,
    formState: { isDirty: isAchFormDirty, isValid: isAchFormValid },
  } = achFormMethods;

  const {
    reset: resetCheck,
    formState: { isDirty: isCheckFormDirty, isValid: isCheckFormValid },
  } = checkFormMethods;

  const achValues = achFormMethods.watch();
  const checkValues = checkFormMethods.watch();

  const savedAchDetails = source.achDetails ?? null;
  const savedCheckDetails = source.mailingAddress ?? null;
  const hasAchDetails = hasSavedAchDetails(savedAchDetails);
  const hasCheckDetails = hasSavedCheckDetails(savedCheckDetails);
  const hasAnyData = hasAchDetails || hasCheckDetails;

  const accountNumber = achValues.accountNumber ?? '';
  const confirmAccountNumber = achValues.confirmAccountNumber ?? '';
  const hasTypedAccountNumber = accountNumber.trim().length > 0;
  const hasAccountNumberMismatch =
    !hasAchDetails && confirmAccountNumber.length > 0 && accountNumber !== confirmAccountNumber;
  const isAccountNumberValid = hasAchDetails
    ? true
    : hasTypedAccountNumber && accountNumber === confirmAccountNumber;

  const preferredPayoutMethod = source.preferredMethod;
  const isMethodDirty = !!selectedMethod && selectedMethod !== preferredPayoutMethod;

  const achStatusChip = getPayoutStatusChip(source.bankAccountStatus);
  const checkStatusChip = getPayoutStatusChip(source.mailingAddressStatus);
  const hasAchProvisioningError = isProvisioningErrorStatus(
    source.bankAccountPassportIntegration?.provisioningStatus,
  );
  const hasCheckProvisioningError = isProvisioningErrorStatus(
    source.mailingAddressPassportIntegration?.provisioningStatus,
  );
  const hasAchCriticalProvisioningError = isCriticalProvisioningStatus(
    source.bankAccountPassportIntegration?.provisioningStatus,
  );
  const hasCheckCriticalProvisioningError = isCriticalProvisioningStatus(
    source.mailingAddressPassportIntegration?.provisioningStatus,
  );
  const achProvisioningError = hasAchProvisioningError
    ? source.bankAccountPassportIntegration?.provisioningError
    : null;
  const checkProvisioningError = hasCheckProvisioningError
    ? source.mailingAddressPassportIntegration?.provisioningError
    : null;
  const selectedProvisioningError =
    selectedMethod === 'ACH'
      ? achProvisioningError
      : selectedMethod === 'CHECK'
        ? checkProvisioningError
        : null;
  const hasSelectedCriticalProvisioningError =
    (selectedMethod === 'ACH' && hasAchCriticalProvisioningError) ||
    (selectedMethod === 'CHECK' && hasCheckCriticalProvisioningError);
  const canSaveMethodChange = isMethodDirty && !hasSelectedCriticalProvisioningError;

  const isCreateBankAccountBlocked = !hasAchDetails && source.hasAssignedBankAccount;
  const isCreateMailingAddressBlocked = !hasCheckDetails && source.hasAssignedMailingAddress;

  const achSaveDisabled =
    isCreateBankAccountBlocked ||
    !isAchFormValid || !isAccountNumberValid || (!isAchFormDirty && !canSaveMethodChange) || saving;
  const checkSaveDisabled =
    isCreateMailingAddressBlocked ||
    !isCheckFormValid || (!isCheckFormDirty && !canSaveMethodChange) || saving;

  const maskedAccountNumber = getMaskedAccountDisplay(
    savedAchDetails?.accountNumber,
    savedAchDetails?.accountNumberLast4,
  );
  const accountNumberDisplay = maskedAccountNumber;

  const accountTypeOptions = ACCOUNT_TYPES.map((type) => ({ id: type, label: type }));

  const methodDiffRows: DiffRow[] =
    selectedMethod && selectedMethod !== preferredPayoutMethod
      ? [{ label: 'Payout Method', prior: preferredPayoutMethod ?? '—', next: selectedMethod }]
      : [];

  const modalDiffRows: DiffRow[] =
    modalContext === 'ach'
      ? [
        ...methodDiffRows,
        {
          label: 'Account Holder Name',
          prior: savedAchDetails?.accountHolderName ?? '—',
          next: achValues.accountHolderName ?? '',
        },
        {
          label: 'Account Type',
          prior: savedAchDetails?.accountType ?? '—',
          next: achValues.accountType ?? '',
        },
        {
          label: 'Routing Number',
          prior: savedAchDetails?.routingNumber ?? '—',
          next: achValues.routingNumber ?? '',
        },
        ...(hasTypedAccountNumber
          ? [
            {
              label: 'Account Number',
              prior:
                getMaskedAccountDisplay(
                  savedAchDetails?.accountNumber,
                  savedAchDetails?.accountNumberLast4,
                ) || '—',
              next: `•••• ${accountNumber.slice(-4)}`,
            },
          ]
          : []),
      ]
      : [
        ...methodDiffRows,
        { label: 'Street 1', prior: savedCheckDetails?.line1 ?? '—', next: checkValues.line1 ?? '' },
        {
          label: 'Street 2',
          prior: savedCheckDetails?.line2 ?? '—',
          next: checkValues.line2 || '—',
        },
        { label: 'City', prior: savedCheckDetails?.city ?? '—', next: checkValues.city ?? '' },
        {
          label: 'State',
          prior: getStateLabel(savedCheckDetails?.state) || '—',
          next: getStateLabel(checkValues.state),
        },
        { label: 'ZIP', prior: savedCheckDetails?.zip ?? '—', next: checkValues.zip ?? '' },
        { label: 'Phone Number', prior: savedCheckDetails?.phoneNumber ?? '—', next: checkValues.phone ?? '' },
      ];

  const changedRows = modalDiffRows.filter((row) => row.prior !== row.next);

  // A client can hold both an ACH account and a mailing address; show both when viewing.
  // Editing still works on one method at a time.
  const showBothMethods = !isEditing && hasAchDetails && hasCheckDetails;
  const showAchSection = showBothMethods || selectedMethod === 'ACH';
  const showCheckSection = showBothMethods || selectedMethod === 'CHECK';
  const provisioningErrors = (
    showBothMethods ? [achProvisioningError, checkProvisioningError] : [selectedProvisioningError]
  ).filter((error): error is string => !!error);

  const visiblePayoutMethods: PayoutMethod[] =
    hasAnyData && selectedMethod && !isEditing ? [selectedMethod] : ['ACH', 'CHECK'];

  const renderPreferredChip = (method: PayoutMethod) =>
    showBothMethods && preferredPayoutMethod === method ? (
      <StatusChip label="Preferred" color="rgba(255, 255, 255, 0.5)" />
    ) : null;

  useEffect(() => {
    resetAch({
      accountHolderName: source.achDetails?.accountHolderName ?? '',
      accountType: source.achDetails?.accountType ?? '',
      routingNumber: source.achDetails?.routingNumber ?? '',
      accountNumber: '',
      confirmAccountNumber: '',
    });
    resetCheck({
      line1: source.mailingAddress?.line1 ?? '',
      line2: source.mailingAddress?.line2 ?? '',
      city: source.mailingAddress?.city ?? '',
      state: source.mailingAddress?.state ?? '',
      zip: source.mailingAddress?.zip ?? '',
      phone: source.mailingAddress?.phoneNumber ?? '',
    });
    if (!isEditing) {
      setSelectedMethod(source.preferredMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const handleStartEdit = () => {
    if (!selectedMethod) {
      // Both methods may be on file without a preferred one; start editing whichever exists.
      if (!hasAnyData) return;
      setSelectedMethod(hasAchDetails ? 'ACH' : 'CHECK');
    }
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    resetAch({
      accountHolderName: savedAchDetails?.accountHolderName ?? '',
      accountType: savedAchDetails?.accountType ?? '',
      routingNumber: savedAchDetails?.routingNumber ?? '',
      accountNumber: '',
      confirmAccountNumber: '',
    });
    resetCheck({
      line1: savedCheckDetails?.line1 ?? '',
      line2: savedCheckDetails?.line2 ?? '',
      city: savedCheckDetails?.city ?? '',
      state: savedCheckDetails?.state ?? '',
      zip: savedCheckDetails?.zip ?? '',
      phone: savedCheckDetails?.phoneNumber ?? '',
    });
    setSelectedMethod(source.preferredMethod);
    setIsEditing(false);
    setNotifyOnAch(true);
    setNotifyOnAddress(true);
    onCancel?.();
  };

  const handleMethodClick = (method: PayoutMethod) => {
    if (!canEdit) return;
    setSelectedMethod(method);
    if (!hasAnyData) {
      setIsEditing(true);
    }
  };

  const openModal = (context: ModalContext) => {
    setModalContext(context);
    setIsModalOpen(true);
  };

  const completeSave = (message: string) => {
    setIsEditing(false);
    setIsModalOpen(false);
    setNotifyOnAch(true);
    setNotifyOnAddress(true);
    setAlert((prev) => ({ ...prev, message, type: 'success' }));
    onSaved();
  };

  const handleDirectSave = async (context: ModalContext) => {
    if (!clientId || !selectedMethod || !canEdit) return;

    setSaving(true);

    try {
      if (context === 'ach') {
        const { accountHolderName, accountType, routingNumber, accountNumber: accNum } =
          achFormMethods.getValues();
        const achDraft: AchDraft = { accountHolderName, accountType, routingNumber, accountNumber: accNum };

        await createPayoutAch(
          clientId,
          buildAchCreatePayload(achDraft, selectedMethod, notifyOnAch),
          settlementId,
        );
      } else {
        const { line1, line2, city, state, zip, phone } = checkFormMethods.getValues();
        const checkDraft: CheckDraft = { line1, line2, city, state, zip, phone };

        await createPayoutCheck(
          clientId,
          buildCheckPayload(checkDraft, selectedMethod, notifyOnAddress),
          settlementId,
        );
      }

      completeSave('Payout details saved successfully.');
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Error. Something went wrong...',
        type: 'error',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmSave = async (reasonCode: string, note: string) => {
    if (!clientId || !selectedMethod || !canEdit) return;

    setSaving(true);

    try {
      const auditNote = note.trim() || 'Request';

      if (modalContext === 'ach') {
        const { accountHolderName, accountType, routingNumber, accountNumber: accNum } =
          achFormMethods.getValues();
        const achDraft: AchDraft = { accountHolderName, accountType, routingNumber, accountNumber: accNum };

        if (hasAchDetails) {
          await updatePayoutAch(
            clientId,
            buildAchUpdatePayload(achDraft, reasonCode, auditNote, notifyOnAch),
            settlementId,
          );
        } else {
          await createPayoutAch(
            clientId,
            buildAchCreatePayload(achDraft, selectedMethod, notifyOnAch, reasonCode, auditNote),
            settlementId,
          );
        }
      } else {
        const { line1, line2, city, state, zip, phone } = checkFormMethods.getValues();
        const checkDraft: CheckDraft = { line1, line2, city, state, zip, phone };

        if (hasCheckDetails) {
          await updatePayoutCheck(
            clientId,
            buildCheckUpdatePayload(checkDraft, reasonCode, auditNote, notifyOnAddress),
            settlementId,
          );
        } else {
          await createPayoutCheck(
            clientId,
            buildCheckPayload(checkDraft, selectedMethod, notifyOnAddress, reasonCode, auditNote),
            settlementId,
          );
        }
      }

      completeSave('Payout details updated successfully.');
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert((prev) => ({
        ...prev,
        message: axiosError.response?.data?.message || 'Error. Something went wrong...',
        type: 'error',
      }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        flexWrap="wrap"
        gap="16px"
      >
        <Box>
          <Typography className={styles.classes.fieldHeadline}>
            Payout Method &amp; Details
          </Typography>
          {canEdit && (isEditing || !hasAnyData) && (
            <Typography className={styles.classes.formDescription}>
              Choose how this plaintiff will be paid. Only the details required for the selected
              method are shown below.
            </Typography>
          )}
        </Box>
        {canEdit && hasAnyData && !isEditing && (
          <Button
            className={styles.classes.editButton}
            disableRipple
            onClick={handleStartEdit}
            startIcon={<EditOutlinedIcon sx={{ fontSize: '16px' }} />}
          >
            Edit
          </Button>
        )}
      </Box>

      {/* With both methods side by side, the column headings already name each method. */}
      {!showBothMethods && (
        <Box display="flex" flexWrap="wrap" gap="12px" mt={canEdit && (isEditing || !hasAnyData) ? '20px' : '12px'}>
          {visiblePayoutMethods.map((method) =>
            canEdit && (isEditing || !hasAnyData) ? (
              <Button
                key={method}
                className={clsx(
                  styles.classes.payoutMethodButton,
                  selectedMethod === method && styles.classes.activePayoutMethodButton,
                )}
                disableRipple
                onClick={() => handleMethodClick(method)}
              >
                {method}
              </Button>
            ) : (
              <Typography key={method} className={styles.classes.payoutMethodLabel}>
                {method}
              </Typography>
            ),
          )}
        </Box>
      )}

      {provisioningErrors.map((error) => (
        <Box key={error} className={styles.classes.provisioningErrorBanner} mt="20px">
          <WarningAmberOutlinedIcon
            sx={{ color: 'rgba(255, 217, 0, 0.9)', flexShrink: 0, fontSize: '18px' }}
          />
          <Typography className={styles.classes.provisioningErrorText}>{error}</Typography>
        </Box>
      ))}

      {!showAchSection && !showCheckSection ? (
        <Box className={styles.classes.warningBanner} mt="20px">
          <WarningAmberOutlinedIcon
            sx={{ color: 'rgba(255, 217, 0, 0.8)', flexShrink: 0, fontSize: '18px' }}
          />
          <Typography className={styles.classes.warningBannerText}>
            Select a payout method to continue.
          </Typography>
        </Box>
      ) : (
        <>
          <Divider
            sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: canEdit && (isEditing || !hasAnyData) ? '24px' : '16px' }}
          />

          <Box className={showBothMethods ? styles.classes.methodColumns : undefined}>
          {showAchSection && (
            <FormProvider key="ach-form" {...achFormMethods}>
              <Box className={styles.classes.formFieldsContainer}>
                <Box className={styles.classes.sectionHeader} sx={!isEditing ? { mb: '8px' } : undefined}>
                  <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
                    <Typography className={styles.classes.sectionHeadline}>ACH Details</Typography>
                    {achStatusChip && (
                      <StatusChip label={achStatusChip.label} color={achStatusChip.color} />
                    )}
                    {renderPreferredChip('ACH')}
                  </Box>
                  <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
                    {canEdit && isEditing && (
                      <FormControlLabel
                        className={styles.classes.notifyToggleRoot}
                        control={
                          <Switch
                            checked={notifyOnAch}
                            className={clsx(
                              notifyOnAch
                                ? styles.classes.notifyActiveToggle
                                : styles.classes.notifyInactiveToggle,
                            )}
                            disableRipple
                            onChange={(e) => setNotifyOnAch(e.target.checked)}
                          />
                        }
                        label="Notify claimant for ACH"
                      />
                    )}
                  </Box>
                </Box>

                {isEditing && isCreateBankAccountBlocked && (
                  <Box className={styles.classes.warningBanner} mt="8px">
                    <WarningAmberOutlinedIcon
                      sx={{ color: 'rgba(255, 217, 0, 0.8)', flexShrink: 0, fontSize: '18px' }}
                    />
                    <Typography className={styles.classes.warningBannerText}>
                      A bank account is already assigned to a settlement. Edit the existing account via the settlement&apos;s payout panel.
                    </Typography>
                  </Box>
                )}

                <Box className={styles.classes.formFieldWrapper}>
                  <FormTextField
                    name="accountHolderName"
                    label="Account Holder Name *"
                    disabled={!isEditing}
                  />
                </Box>

                <Box className={styles.classes.formInfoContainer}>
                  <Box flex={1}>
                    <FormSelectField
                      name="accountType"
                      label="Account Type *"
                      options={accountTypeOptions}
                      disabled={!isEditing}
                      width="100%"
                    />
                  </Box>
                  <Box flex={1}>
                    <FormTextField
                      name="routingNumber"
                      label="Routing Number *"
                      disabled={!isEditing}
                      inputProps={{ maxLength: 9 }}
                      handleChange={(value) => {
                        const digits = value.replace(/\D/g, '').slice(0, 9);
                        if (value !== digits) {
                          achFormMethods.setValue('routingNumber', digits, {
                            shouldDirty: true,
                            shouldValidate: true,
                          });
                        }
                      }}
                    />
                  </Box>
                </Box>

                {isEditing ? (
                  <>
                    {hasAchDetails && (
                      <Box className={styles.classes.formFieldWrapper}>
                        <TextField
                          className={styles.classes.displayTextField}
                          disabled
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          label="Account Number *"
                          value={accountNumberDisplay}
                          variant="outlined"
                        />
                      </Box>
                    )}
                    <Box className={styles.classes.formFieldWrapper}>
                      <FormTextField
                        name="accountNumber"
                        label={hasAchDetails ? 'New Account Number *' : 'Account Number *'}
                        disabled={false}
                      />
                    </Box>
                    {!hasAchDetails && (
                      <Box className={styles.classes.formFieldWrapper}>
                        <FormTextField
                          name="confirmAccountNumber"
                          label="Confirm Account Number *"
                          disabled={false}
                        />
                        {hasAccountNumberMismatch && (
                          <Typography color="error" fontSize="12px" ml="14px" mt="4px">
                            Account numbers do not match.
                          </Typography>
                        )}
                      </Box>
                    )}
                  </>
                ) : (
                  <Box className={styles.classes.formFieldWrapper}>
                    <TextField
                      className={styles.classes.displayTextField}
                      disabled
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      label="Account Number *"
                      value={accountNumberDisplay}
                      variant="outlined"
                    />
                  </Box>
                )}

                {isEditing && (
                  <Box className={styles.classes.cardActions}>
                    <Box width="100%">
                      <FormSubmitButton
                        buttonIcon={<SaveIcon />}
                        buttonText="Save"
                        disabled={achSaveDisabled}
                        onClick={() => (hasAnyData ? openModal('ach') : handleDirectSave('ach'))}
                      />
                    </Box>
                    <Box width="100%">
                      <FormCancelButton disabled={saving} onClick={handleCancelEdit} />
                    </Box>
                  </Box>
                )}
              </Box>
            </FormProvider>
          )}

          {showCheckSection && (
            <FormProvider key="check-form" {...checkFormMethods}>
              <Box
                className={clsx(
                  styles.classes.formFieldsContainer,
                  showBothMethods && styles.classes.secondMethodColumn,
                )}
              >
                <Box
                  className={styles.classes.sectionHeader}
                  sx={showBothMethods ? { mb: '8px' } : undefined}
                >
                  <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
                    <Typography className={styles.classes.sectionHeadline}>
                      Mailing Address Details
                    </Typography>
                    {checkStatusChip && (
                      <StatusChip label={checkStatusChip.label} color={checkStatusChip.color} />
                    )}
                    {renderPreferredChip('CHECK')}
                  </Box>
                  <Box display="flex" alignItems="center" gap="12px" flexWrap="wrap">
                    {canEdit && isEditing && (
                      <FormControlLabel
                        className={styles.classes.notifyToggleRoot}
                        control={
                          <Switch
                            checked={notifyOnAddress}
                            className={clsx(
                              notifyOnAddress
                                ? styles.classes.notifyActiveToggle
                                : styles.classes.notifyInactiveToggle,
                            )}
                            disableRipple
                            onChange={(e) => setNotifyOnAddress(e.target.checked)}
                          />
                        }
                        label="Notify claimant for CHECK"
                      />
                    )}
                  </Box>
                </Box>

                {isEditing && isCreateMailingAddressBlocked && (
                  <Box className={styles.classes.warningBanner} mt="8px">
                    <WarningAmberOutlinedIcon
                      sx={{ color: 'rgba(255, 217, 0, 0.8)', flexShrink: 0, fontSize: '18px' }}
                    />
                    <Typography className={styles.classes.warningBannerText}>
                      A mailing address is already assigned to a settlement. Edit the existing address via the settlement&apos;s payout panel.
                    </Typography>
                  </Box>
                )}

                <Box className={styles.classes.formFieldWrapper}>
                  <FormTextField name="line1" label="Street 1 *" disabled={!isEditing} />
                </Box>
                <Box className={styles.classes.formFieldWrapper}>
                  <FormTextField
                    name="line2"
                    label="Street 2 (optional)"
                    disabled={!isEditing}
                  />
                </Box>

                <Box className={styles.classes.formInfoContainer}>
                  <Box flex={1}>
                    <FormTextField name="city" label="City *" disabled={!isEditing} />
                  </Box>
                  <Box flex={1}>
                    <FormSelectField
                      name="state"
                      label="State *"
                      options={US_STATES_LIST}
                      disabled={!isEditing}
                      width="100%"
                    />
                  </Box>
                </Box>

                <Box
                  className={styles.classes.formInfoContainer}
                  sx={isEditing ? { pb: '24px' } : undefined}
                >
                  <Box
                    flex={1}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        bottom: 'unset !important',
                        top: 'calc(100% + 4px) !important',
                      },
                    }}
                  >
                    <FormTextField name="zip" label="ZIP Code *" disabled={!isEditing} />
                  </Box>
                  <Box
                    flex={1}
                    sx={{
                      '& .MuiFormHelperText-root': {
                        bottom: 'unset !important',
                        top: 'calc(100% + 4px) !important',
                      },
                    }}
                  >
                    <FormMaskedTextField
                      name="phone"
                      label="Phone Number *"
                      mask="+19999999999"
                      disabled={!isEditing}
                    />
                  </Box>
                </Box>

                {isEditing && (
                  <Box className={styles.classes.cardActions}>
                    <Box width="100%">
                      <FormSubmitButton
                        buttonIcon={<SaveIcon />}
                        buttonText="Save"
                        disabled={checkSaveDisabled}
                        onClick={() =>
                          hasAnyData ? openModal('check') : handleDirectSave('check')
                        }
                      />
                    </Box>
                    <Box width="100%">
                      <FormCancelButton disabled={saving} onClick={handleCancelEdit} />
                    </Box>
                  </Box>
                )}
              </Box>
            </FormProvider>
          )}
          </Box>
        </>
      )}

      <ClientPayoutSaveConfirmDialog
        changedRows={changedRows}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirmSave}
        open={isModalOpen}
        reasonCodes={reasonCodes}
        saving={saving}
      />
    </>
  );
};

export default ClientPayoutFormContainer;